import { Add, ArrowBack, ChevronRight } from '@mui/icons-material';
import {
  Divider,
  IconButton,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { AssortmentTag } from '@retail/my-assortment/types';
import { useProductsTFunction } from '@retail/products/i18n';
import { SupplierItemAssortmentTag } from '@retail/products/types';
import { notBeforeToday } from '@retail/utils';
import { ContainedButton, TextButton } from '@shared/components';
import { useState } from 'react';
import { TagDateFields } from './TagDateFields';

interface Props {
  assortmentTags: AssortmentTag[];
  excludedTagIds?: number[];
  tooltip?: string;
  handleTagChange: (tag: SupplierItemAssortmentTag) => void;
}

/**
 * Brand level add menu: pick a sub-assortment, then set its dates before it is added, so
 * the tag never lands on the items with dates the user did not choose.
 */
export function AddTagWithDatesMenu({
  assortmentTags,
  excludedTagIds,
  tooltip,
  handleTagChange,
}: Props) {
  const t = useProductsTFunction();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tagToAdd, setTagToAdd] = useState<SupplierItemAssortmentTag>();

  const selectableTags = excludedTagIds
    ? assortmentTags.filter((tag) => !excludedTagIds.includes(tag.id))
    : assortmentTags;
  const assortmentTagToAdd = assortmentTags.find((tag) => tag.id === tagToAdd?.tagId);

  const onClose = () => {
    setAnchorEl(null);
    setTagToAdd(undefined);
  };

  return (
    <>
      <Tooltip title={tooltip ?? ''}>
        <IconButton sx={{ p: 0 }} onClick={(event) => setAnchorEl(event.currentTarget)}>
          <Add fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {tagToAdd ? (
          <Stack gap={2} p={3} maxWidth={440}>
            <Stack flexDirection="row" alignItems="center" gap={1}>
              <Tooltip title={t('products.mgItem.assortmentTags.chooseOtherTag')}>
                <IconButton size="small" onClick={() => setTagToAdd(undefined)}>
                  <ArrowBack fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="h6">{tagToAdd.tagName}</Typography>
            </Stack>

            <TagDateFields
              tag={tagToAdd}
              assortmentTag={assortmentTagToAdd}
              onChange={setTagToAdd}
            />

            <Divider />
            <Stack flexDirection="row" justifyContent="flex-end" gap={1}>
              <TextButton size="small" onClick={onClose}>
                {t('products.cancel')}
              </TextButton>
              <ContainedButton
                size="small"
                onClick={() => {
                  handleTagChange(tagToAdd);
                  onClose();
                }}
              >
                {t('products.add')}
              </ContainedButton>
            </Stack>
          </Stack>
        ) : (
          <MenuList>
            {selectableTags.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body3">
                  {t('products.mgItem.assortmentTags.noTags')}
                </Typography>
              </MenuItem>
            ) : (
              selectableTags.map((tag) => (
                <MenuItem
                  key={tag.id}
                  sx={{ justifyContent: 'space-between', gap: 2 }}
                  onClick={() =>
                    setTagToAdd(
                      new SupplierItemAssortmentTag({
                        tagId: tag.id,
                        tagName: tag.name,
                        validFrom: notBeforeToday(tag.validFrom),
                        validTo: tag.validTo,
                      })
                    )
                  }
                >
                  {tag.name}
                  <ChevronRight fontSize="small" color="action" />
                </MenuItem>
              ))
            )}
          </MenuList>
        )}
      </Popover>
    </>
  );
}
